import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { Dispatch } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import {
  formatAssetDispositionLabel,
  getAssetDisplayNames,
  getProvisionAssetCounts,
  getTransferMethodLabel,
  hasOnlyInsurancePolicies,
  isAssetDP,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.formatting';
import { DispositiveProvisions_DispositiveProvisionFragment } from '@/modules/dispositiveProvisions/graphql/DispositiveProvisions.fragments.generated';
import {
  NonTrustCharitableEntityDetailsTabs,
  PersonalAccountDetailsTabs,
  TrustDetailsTabs,
} from '@/modules/entities/details/entityDetails.types';
import {
  NON_TRUST_CHARITABLE_ENTITY_TYPES,
  PERSONAL_FAMILY_ACCOUNT_TYPES,
} from '@/modules/entities/entities.constants';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useEstateWaterfallContext } from '@/modules/estateWaterfall/contexts/estateWaterfall.context';
import { ClientProfileTabs } from '@/modules/summaryPanels/ClientProfileSummaryPanel/ClientProfileSummaryPanel.types';
import { TestamentaryEntityTabs } from '@/modules/summaryPanels/TestamentaryEntitySummaryPanel/TestamentaryEntitySummaryPanel.types';
import { AfterDeath } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfallAction } from '../../../types/actions';
import { createEdgeId } from '../../../waterfallGraph/waterfallGraph';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface AddDispositionButtonProps {
  entityType: EntityType | null;
  dispatch: Dispatch<EstateWaterfallAction>;
  source: string;
  target: string;
  dispositionEligible: boolean;
  sourceIsTestamentaryEntity: boolean;
  sourceIsClientProfile: boolean;
}

function AddDispositionButton({
  source,
  target,
  entityType,
  dispatch,
  dispositionEligible,
  sourceIsTestamentaryEntity,
  sourceIsClientProfile,
}: AddDispositionButtonProps) {
  const { state } = useEstateWaterfallContext();

  const edgeId = createEdgeId(source, target);

  const handleOpenDispositions = () => {
    if (!entityType) {
      return;
    }

    if (NON_TRUST_CHARITABLE_ENTITY_TYPES.includes(entityType)) {
      dispatch({
        type: 'OPEN_SUMMARY_PANEL',
        nodeId: source,
        initialTab: NonTrustCharitableEntityDetailsTabs.DISPOSITIONS,
      });
    } else if (PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType)) {
      dispatch({
        type: 'OPEN_SUMMARY_PANEL',
        nodeId: source,
        initialTab: PersonalAccountDetailsTabs.DISPOSITIONS,
      });
    } else {
      dispatch({
        type: 'OPEN_SUMMARY_PANEL',
        nodeId: source,
        initialTab: TrustDetailsTabs.DISPOSITIONS,
      });
    }

    // Close the tooltip after opening the panel
    dispatch({ type: 'CLOSE_EDGE_LABEL', edgeId });
  };

  const shouldHide = !dispositionEligible || state.presentationMode;

  if (shouldHide) {
    return null;
  }

  return (
    <Button
      sx={{
        width: 'fit-content',
      }}
      variant="primary"
      external
      size="xs"
      onClick={() => {
        if (sourceIsTestamentaryEntity) {
          dispatch({
            type: 'OPEN_SUMMARY_PANEL',
            nodeId: source,
            initialTab: TestamentaryEntityTabs.Disposition,
          });
        } else if (sourceIsClientProfile) {
          dispatch({
            type: 'OPEN_SUMMARY_PANEL',
            nodeId: source,
            initialTab: ClientProfileTabs.Disposition,
          });
        } else {
          handleOpenDispositions();
          return; // handleOpenDispositions already closes the tooltip
        }

        // Close the tooltip after opening the panel
        dispatch({ type: 'CLOSE_EDGE_LABEL', edgeId });
      }}
    >
      Add dispositions
    </Button>
  );
}

/**
 * Builds additional MiniTable rows for asset DPs associated with an edge.
 * Returns an empty array when there are no asset DPs.
 */
function getAssetDPRows(
  provisions: DispositiveProvisions_DispositiveProvisionFragment[] | undefined
): MiniTableRow[] {
  if (!provisions) return [];

  const assetProvisions = provisions.filter(isAssetDP);
  if (assetProvisions.length === 0) return [];

  return compact(
    assetProvisions.map((p) => ({
      label: getTransferMethodLabel(p),
      value: formatAssetDispositionLabel({
        pct: p.dispositionPercentage ?? new Decimal(100),
        assetNames: getAssetDisplayNames(p),
        transferMethod: p.transferMethod,
        isInsuranceOnly: hasOnlyInsurancePolicies(getProvisionAssetCounts(p)),
      }),
      labelProps: DISPOSITION_LABEL_PROPS,
      valueProps: DISPOSITION_VALUE_PROPS,
    }))
  );
}

interface DispositionCardParams {
  source: string;
  target: string;
  edgeValue: Decimal;
  sourceEntityName: string;
  targetEntityName: string;
  decedentName?: string;
  isEdgeToSelf: boolean;
  entityHasDispositions: boolean;
  sourceIsTestamentaryEntity: boolean;
  sourceIsClientProfile: boolean;
  entityType: EntityType | null;
  dispatch: Dispatch<EstateWaterfallAction>;
  sourceIsGroup: boolean;
  targetIsGroup: boolean;
  currentSection: AfterDeath;
  dispositionHappensInCurrentSection: boolean;
  /** Individual provisions associated with this edge, used to detect asset DPs. */
  associatedProvisions?: DispositiveProvisions_DispositiveProvisionFragment[];
}

/** Shared label props to ensure consistent column alignment across all rows. */
const DISPOSITION_LABEL_PROPS = { sx: { minWidth: 40 } } as const;
/** Right-align values so wrapped text stays flush-right, reducing visual jitter. */
const DISPOSITION_VALUE_PROPS = { sx: { textAlign: 'right' } } as const;

export function createDispositionCard({
  source,
  target,
  edgeValue,
  sourceEntityName,
  targetEntityName,
  decedentName,
  isEdgeToSelf,
  entityHasDispositions,
  sourceIsTestamentaryEntity,
  sourceIsClientProfile,
  entityType,
  dispatch,
  sourceIsGroup,
  targetIsGroup,
  currentSection,
  dispositionHappensInCurrentSection,
  associatedProvisions,
}: DispositionCardParams): CardConfig | null {
  const dispositionEligible =
    sourceIsTestamentaryEntity || sourceIsClientProfile || entityType !== null;

  // If the source is a group, don't show a disposition card
  if (sourceIsGroup) {
    return null;
  }

  // If the dispositions are happening now, show a disposition card
  if (
    entityHasDispositions &&
    dispositionHappensInCurrentSection &&
    !isEdgeToSelf
  ) {
    const assetRows = getAssetDPRows(associatedProvisions);

    return {
      element: (
        <EdgeLabelCard
          key={`disposition-${source}-${target}`}
          title="Dispositive provision"
          description={
            targetIsGroup
              ? `${sourceEntityName} distributes upon ${decedentName}'s death.`
              : `${sourceEntityName} distributes to ${targetEntityName} upon ${decedentName}'s death.`
          }
          rows={[
            {
              label: 'Distribution amount',
              value: formatCurrencyNoDecimals(edgeValue),
              labelProps: DISPOSITION_LABEL_PROPS,
              valueProps: DISPOSITION_VALUE_PROPS,
            },
            ...assetRows,
          ]}
        />
      ),
      label: 'Disposition',
      value: formatCurrencyNoDecimals(edgeValue),
      variant: 'primary',
    };
  }

  // If the dispositions are happening in another section, show a when the dispositions happens
  if (entityHasDispositions && !dispositionHappensInCurrentSection) {
    return {
      element: (
        <EdgeLabelCard
          key={`disposition-${source}-${target}`}
          title="Dispositive provision"
          description={`${sourceEntityName} disposes after ${currentSection === AfterDeath.None ? '2nd' : '1st'} death.`}
          rows={[
            {
              label: 'Remaining value',
              value: formatCurrencyNoDecimals(edgeValue),
            },
          ]}
        />
      ),
      label: '',
      value: formatCurrencyNoDecimals(edgeValue),
      variant: 'primary',
    };
  }

  if (!entityHasDispositions && dispositionEligible) {
    return {
      element: (
        <EdgeLabelCard
          key={`disposition-${source}-${target}`}
          description={`No dispositions entered for ${sourceEntityName}.`}
          rows={[
            {
              label: 'Remaining value',
              value: formatCurrencyNoDecimals(edgeValue),
            },
          ]}
        >
          <AddDispositionButton
            source={source}
            target={target}
            entityType={entityType}
            dispatch={dispatch}
            dispositionEligible={dispositionEligible}
            sourceIsTestamentaryEntity={sourceIsTestamentaryEntity}
            sourceIsClientProfile={sourceIsClientProfile}
          />
        </EdgeLabelCard>
      ),
      label: '',
      value: formatCurrencyNoDecimals(edgeValue),
      variant: 'primary',
    };
  }

  // Finally, if the edge is to self, show a card that says the source does not fully distribute
  if (isEdgeToSelf && dispositionEligible) {
    return {
      element: (
        <EdgeLabelCard
          key={`disposition-${source}-${target}`}
          description={`${sourceEntityName} does not fully distribute upon ${decedentName}'s death.`}
          rows={[
            {
              label: 'Remaining value',
              value: formatCurrencyNoDecimals(edgeValue),
            },
          ]}
        >
          <AddDispositionButton
            source={source}
            target={target}
            entityType={entityType}
            dispatch={dispatch}
            dispositionEligible={dispositionEligible}
            sourceIsTestamentaryEntity={sourceIsTestamentaryEntity}
            sourceIsClientProfile={sourceIsClientProfile}
          />
        </EdgeLabelCard>
      ),
      label: '',
      value: formatCurrencyNoDecimals(edgeValue),
      variant: 'primary',
    };
  }

  return null;
}
